import { Color, Spacing } from '@persona-id/design-tokens';
import { AiFillIcon, CaretDownIcon } from '@persona-id/design-tokens/icons';
import { useSelector } from 'react-redux';
import documentClassificationsJson from 'shared/documents/document_classifications.json';
import styled from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import ClassificationsPresetModal, {
  SupportedCountries,
} from 'dashboard/components/ClassificationsConfig/ClassificationsPresetModal';
import ContextMenu from 'dashboard/components/ContextMenu';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import useLearnMoreEventClick from 'dashboard/components/ProductUpsellTooltip/useLearnMoreEventClick';
import {
  Classification,
  ClassificationMethod,
  DefinitionType,
  ToggleOption,
} from 'dashboard/models/document';
import { OrganizationPlanKey } from 'dashboard/models/organization-plan';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Radio from 'design-system/components/Radio';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useToggle from 'lib/hooks/useToggle';

const TableContainer = styled.div`
  max-height: 512px;
  overflow-y: auto;
`;

const EmptyTable = ({ colSpan, children }: { colSpan: number; children: React.ReactNode }) => (
  <TableRow style={{ height: '80px' }}>
    <TableCell colSpan={colSpan}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </TableCell>
  </TableRow>
);

const LearnMoreTable = ({
  id,
  accessTier,
  colSpan,
}: {
  id: string;
  accessTier: Exclude<EditorAccessTier, EditorAccessTier.Any>;
  colSpan: number;
}) => {
  const { linkElementId, onLearnMoreClick } = useLearnMoreEventClick({ id });

  return (
    <EmptyTable colSpan={colSpan}>
      <Label weight='bold'>This feature is not available on your plan</Label>
      <Spacer height={0.5} />
      <a
        href='#'
        target='_blank'
        id={linkElementId}
        rel='noreferrer'
        onClick={(e) => {
          e.preventDefault();
          onLearnMoreClick({ accessTier });
        }}
        style={{ textDecoration: 'underline' }}
      >
        Learn more
      </a>
    </EmptyTable>
  );
};

const ActivableTableRow = styled(TableRow)`
  background-color: ${({ theme, active }) => active && theme.color.background.highlight};
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
`;

// TODO: [Documents] Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  width: fit-content;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

interface ClassificationsConfigProps {
  classifications: Classification[];
  selected: number | null;
  accepted: number[];
  onReplace: (indices: number[], classifications: Classification[]) => void;
  onSelect: (index?: number | null) => void;
  onToggleAccepted: (classification: Classification, accepted: boolean) => void;
  onDelete: (index: number) => void;
  editable: boolean;
}

const ClassificationsConfig = ({
  classifications,
  selected,
  accepted,
  onReplace,
  onSelect,
  onToggleAccepted,
  onDelete,
  editable,
}: ClassificationsConfigProps) => {
  // TODO(Documents): *DO NOT COPY THIS LOGIC* to check the current plan of the Organization. This logic
  // will be replaced by explicit ProductFeatures that will control this functionality in the near future.
  const currentOrganizationPlan = useSelector(
    (state: RootState) => state.organization.currentOrganizationPlan,
  );
  const planAllowsAccessToEnhancedFeatures =
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Managed ||
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.TrialSalesProspect ||
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Partner ||
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Internal;

  const hasAccessForEnhancedTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.InquiriesEnhanced,
  );

  const [shouldShowPresetModal, { enable: showPresetModal, disable: hidePresetModal }] =
    useToggle(false);

  const handleAdd = () => onSelect(null);

  const handleDelete = (index: number) => {
    onDelete(index);
    if (selected === index) {
      onSelect(undefined);
    } else if (selected != null && selected > index) {
      onSelect(selected - 1);
    }
  };

  const classificationsWithIndex = classifications.map(
    (classification, index) => [classification, index] as const,
  );
  const enabledClassifications = classificationsWithIndex.filter(
    ([classification]) => classification['toggle-option'] === ToggleOption.Enabled,
  );
  const presetClassifications = enabledClassifications
    .filter(([classification]) => classification['definition-type'] === DefinitionType.Preset)
    .sort(([a], [b]) => a.label.localeCompare(b.label));
  const customClassifications = enabledClassifications.filter(
    ([classification]) => classification['definition-type'] === DefinitionType.Custom,
  );

  const handleReplacePreset = (classifications: Classification[]) => {
    const removed = classificationsWithIndex
      .filter(([classification]) =>
        classifications.every(
          (c) =>
            classification['toggle-option'] !== ToggleOption.Enabled ||
            c.key !== classification.key ||
            c['definition-type'] !== classification['definition-type'] ||
            c['classification-method'] !== classification['classification-method'],
        ),
      )
      .map(([, index]) => index);

    onReplace(removed, classifications);
  };

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title
            actions={
              <DropdownMenu
                disabled={!editable || !hasAccessForEnhancedTier}
                trigger={
                  <Button
                    variant='basic'
                    size='sm'
                    suffixIcon={<CaretDownIcon />}
                    disabled={!editable || !hasAccessForEnhancedTier}
                  >
                    Add document type
                  </Button>
                }
              >
                <DropdownMenu.Item
                  id='classifications-config__add-preset'
                  onClick={showPresetModal}
                >
                  Persona document types
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  id='classifications-config__add-custom'
                  onClick={handleAdd}
                  disabled={!planAllowsAccessToEnhancedFeatures}
                >
                  Custom document types
                </DropdownMenu.Item>
              </DropdownMenu>
            }
          >
            Classifications
            {!hasAccessForEnhancedTier && (
              <ProductUpsellTooltip
                accessTier={EditorAccessTier.InquiriesEnhanced}
                learnMoreId='verifications_document_classifications'
                trigger={
                  <div>
                    <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} />
                  </div>
                }
              />
            )}
          </Card.Title>
          <Paragraph>
            Manage how different types of documents are categorized and handled. The result of this
            check will be displayed under &ldquo;Type detection&rdquo; in the verifications overview
            page.
          </Paragraph>
          <Spacer height={0.5} />
          <Label size='lg' weight='bold'>
            <Tooltip
              side='top'
              trigger={<TooltipIndicatorWrapper>Persona document types</TooltipIndicatorWrapper>}
            >
              <Paragraph>
                Select which predefined document types by Persona are accepted or rejected based on
                supported countries.
              </Paragraph>
            </Tooltip>
          </Label>
          <TableContainer>
            <Table>
              <TableHead sticky>
                <TableRow>
                  <TableHeaderCell width='220px'>Label</TableHeaderCell>
                  <TableHeaderCell>Supported countries</TableHeaderCell>
                  <TableHeaderCell width='min'>Accept</TableHeaderCell>
                  <TableHeaderCell width='min'>Reject</TableHeaderCell>
                  <TableHeaderCell width='min' />
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasAccessForEnhancedTier && presetClassifications.length === 0 ? (
                  <LearnMoreTable
                    id='verifications_document_preset_classifications'
                    accessTier={EditorAccessTier.InquiriesEnhanced}
                    colSpan={5}
                  />
                ) : presetClassifications.length === 0 ? (
                  <EmptyTable colSpan={5}>No classifications configured</EmptyTable>
                ) : (
                  presetClassifications.map(([classification, index]) => {
                    const documentClassificationJson = documentClassificationsJson.find(
                      (documentClassificationJson) =>
                        documentClassificationJson.key === classification.key &&
                        documentClassificationJson.definition_type ===
                          classification['definition-type'] &&
                        documentClassificationJson.classification_method ===
                          classification['classification-method'],
                    );

                    return (
                      <TableRow
                        key={`${classification.key}-${classification['definition-type']}-${classification['classification-method']}`}
                      >
                        <TableCell>
                          <span style={{ display: 'flex' }}>
                            {classification.label}
                            {classification['classification-method'] ===
                              ClassificationMethod.Ai && (
                              <>
                                <Spacer height={0} width={0.5} />
                                <AiFillIcon size='12px' color={Color.PrimaryPurple} />
                              </>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          <SupportedCountries
                            supportedCountryCodes={
                              documentClassificationJson?.supported_country_codes || []
                            }
                          />
                        </TableCell>
                        <TableCell width='min'>
                          <Centered>
                            <Radio
                              value='accept'
                              checked={accepted.includes(index)}
                              onChange={() => onToggleAccepted(classification, true)}
                              disabled={!editable || !hasAccessForEnhancedTier}
                            />
                          </Centered>
                        </TableCell>
                        <TableCell width='min'>
                          <Centered>
                            <Radio
                              value='reject'
                              checked={!accepted.includes(index)}
                              onChange={() => onToggleAccepted(classification, false)}
                              disabled={!editable || !hasAccessForEnhancedTier}
                            />
                          </Centered>
                        </TableCell>
                        <TableCell width='min'>
                          <ContextMenu
                            variant='inverse'
                            disabled={!editable || !hasAccessForEnhancedTier}
                            items={[
                              {
                                id: 'classifications-config__context-menu-remove',
                                label: 'Remove',
                                onClick: () => handleDelete(index),
                                variant: 'danger',
                              },
                            ]}
                            renderItem={(item) => (
                              <ContextMenu.Item {...item}>{item.label}</ContextMenu.Item>
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Label size='lg' weight='bold' style={{ display: 'flex', gap: Spacing[2] }}>
            <Tooltip
              side='top'
              trigger={<TooltipIndicatorWrapper>Custom document types</TooltipIndicatorWrapper>}
            >
              <Paragraph>
                Add and configure your own custom document types and specify the acceptance
                criteria.
              </Paragraph>
            </Tooltip>
            {!planAllowsAccessToEnhancedFeatures && hasAccessForEnhancedTier && (
              <ProductUpsellTooltip
                accessTier={EditorAccessTier.InquiriesEnhanced}
                learnMoreId='verifications_document_custom_classifications'
                trigger={
                  <div>
                    <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} />
                  </div>
                }
              />
            )}
          </Label>
          <TableContainer>
            <Table>
              <TableHead sticky>
                <TableRow>
                  <TableHeaderCell width='220px'>Label</TableHeaderCell>
                  <TableHeaderCell>Supported countries</TableHeaderCell>
                  <TableHeaderCell width='min'>Accept</TableHeaderCell>
                  <TableHeaderCell width='min'>Reject</TableHeaderCell>
                  <TableHeaderCell width='min' />
                </TableRow>
              </TableHead>
              <TableBody>
                {!planAllowsAccessToEnhancedFeatures && customClassifications.length === 0 ? (
                  <LearnMoreTable
                    id='verifications_document_preset_classifications'
                    accessTier={EditorAccessTier.InquiriesEnhanced}
                    colSpan={5}
                  />
                ) : customClassifications.length === 0 ? (
                  <EmptyTable colSpan={5}>No classifications configured</EmptyTable>
                ) : (
                  customClassifications.map(([classification, index]) => (
                    <ActivableTableRow
                      key={`${classification.key}-${classification['definition-type']}-${classification['classification-method']}`}
                      id={`classifications-config__custom-row-${index}`}
                      active={index === selected}
                      onClick={
                        editable && planAllowsAccessToEnhancedFeatures
                          ? () => onSelect(index)
                          : undefined
                      }
                    >
                      <TableCell>
                        <span style={{ display: 'flex' }}>
                          {classification.label}
                          {classification['classification-method'] === ClassificationMethod.Ai && (
                            <>
                              <Spacer height={0} width={0.5} />
                              <AiFillIcon size='12px' color={Color.PrimaryPurple} />
                            </>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <SupportedCountries supportedCountryCodes={[]} />
                      </TableCell>
                      <TableCell width='min'>
                        <Centered>
                          <Radio
                            id={`classifications-config__accept-radio-${index}`}
                            value='accept'
                            checked={accepted.includes(index)}
                            onChange={() => onToggleAccepted(classification, true)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!editable || !planAllowsAccessToEnhancedFeatures}
                          />
                        </Centered>
                      </TableCell>
                      <TableCell width='min'>
                        <Centered>
                          <Radio
                            id={`classifications-config__reject-radio-${index}`}
                            value='reject'
                            checked={!accepted.includes(index)}
                            onChange={() => onToggleAccepted(classification, false)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!editable || !planAllowsAccessToEnhancedFeatures}
                          />
                        </Centered>
                      </TableCell>
                      <TableCell width='min'>
                        <ContextMenu
                          variant='inverse'
                          disabled={!editable || !planAllowsAccessToEnhancedFeatures}
                          items={[
                            {
                              id: 'classifications-config__context-menu-delete',
                              label: 'Delete',
                              onClick: () => handleDelete(index),
                              variant: 'danger',
                            },
                          ]}
                          renderItem={(item) => (
                            <ContextMenu.Item {...item}>{item.label}</ContextMenu.Item>
                          )}
                        />
                      </TableCell>
                    </ActivableTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card.Body>
      </Card>
      {shouldShowPresetModal && (
        <ClassificationsPresetModal
          classifications={classifications}
          open={shouldShowPresetModal}
          handleCloseModal={hidePresetModal}
          onConfirm={handleReplacePreset}
        />
      )}
    </>
  );
};

export default ClassificationsConfig;
